import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useMediaQuery, useTheme } from '@material-ui/core';
import ReactSelect from 'react-select';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { format } from 'date-fns';
import pt from 'date-fns/locale/pt-BR';
import 'react-datepicker/dist/react-datepicker.css';

import { useAuth } from 'hooks/auth';
import { useHttp } from 'hooks/http';

import { FilterIcon } from '../../styles/icons';

import Pagination from '../../components/Pagination';
import { ButtonIcon } from '../../components/Forms';
import { Table } from '../../components/Table';
import { IPaginate } from '../../interfaces';

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

import {
  Container,
  FiltersRow,
  TableSection,
  ChartPanel,
  ChartTitle,
  SummaryPanel,
  ChartsColumn,
  PieSummaryRow,
  BarChartPanel,
  ContentGrid,
  LeftColumnPane,
  RightColumnPane,
  TableScrollBox,
} from './styles';
import Laydash from 'components/Layout/Laydash';

registerLocale('pt-BR', pt);

interface IServiceUserOption {
  value: string;
  label: string;
}

interface IManagementOption {
  value: number;
  label: string;
}

interface IAggregatedByUserRow {
  user_id: string;
  first_name: string;
  last_name: string;
  document_secondary: string | null;
  total: number;
}

const REFRESH_MS = 60_000;

const PIE_COLORS = {
  Pessoa: '#2E7D32',
  Veículo: '#1565C0',
};

interface ITypeSummary {
  person_count: number;
  vehicle_count: number;
  total: number;
}

interface IResultSummaryRow {
  name: string;
  count: number;
}

const BAR_PALETTE = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00c49f', '#a28fd0', '#f06292'];

/* eslint-disable @typescript-eslint/no-explicit-any */
const analyticsSelectStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: 40,
    border: '1px solid #ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: 'none',
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: 4,
    zIndex: 20,
  }),
  menuList: (base: any) => ({
    ...base,
    backgroundColor: '#fff',
    padding: 0,
  }),
  option: (base: any, state: { isFocused: boolean; isSelected: boolean }) => ({
    ...base,
    backgroundColor: state.isSelected ? '#e8e8e8' : state.isFocused ? '#f5f5f5' : '#fff',
    color: '#333',
    cursor: 'pointer',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#333',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#666',
  }),
  input: (base: any) => ({
    ...base,
    color: '#333',
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: '#ccc',
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: '#666',
  }),
};

const Analytics: React.FC = () => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { user } = useAuth();
  const { httpGet } = useHttp();

  const [managements, setManagements] = useState<IManagementOption[]>([]);
  const [selectedManagement, setSelectedManagement] = useState<IManagementOption | null>(null);

  const [serviceUserOptions, setServiceUserOptions] = useState<IServiceUserOption[]>([]);
  const [serviceUser, setServiceUser] = useState<IServiceUserOption | null>(null);
  const [draftStart, setDraftStart] = useState<Date>(() => new Date());
  const [draftEnd, setDraftEnd] = useState<Date>(() => new Date());
  const [appliedStart, setAppliedStart] = useState<Date>(() => new Date());
  const [appliedEnd, setAppliedEnd] = useState<Date>(() => new Date());
  const [aggregatedRows, setAggregatedRows] = useState<IAggregatedByUserRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginate, setPaginate] = useState<IPaginate>({
    page: 1, lastPage: 1, perPage: 10,
  });
  const [typeSummary, setTypeSummary] = useState<ITypeSummary | null>(null);
  const [resultSummary, setResultSummary] = useState<IResultSummaryRow[]>([]);

  const managementId = selectedManagement?.value ?? user?.allocation?.management_id;

  const loadServiceUsers = useCallback(async () => {
    if (!managementId) return;
    const { status, data } = await httpGet('orders/analytics/service-users', {
      params: {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
      },
    }, false);
    if (status === 200 && Array.isArray(data)) {
      const opts = data.map((u: {
        id: string;
        first_name: string;
        last_name: string;
        document_secondary: string | null;
      }) => ({
        value: u.id,
        label: [u.document_secondary, u.first_name, u.last_name].filter(Boolean).join(' — '),
      }));
      setServiceUserOptions(opts);
      setServiceUser((prev) => {
        if (!prev) return null;
        return opts.some((o) => o.value === prev.value) ? prev : null;
      });
    }
  }, [httpGet, managementId, appliedStart, appliedEnd]);

  useEffect(() => {
    loadServiceUsers();
  }, [loadServiceUsers]);

  const loadManagements = useCallback(async () => {
    const { status, data } = await httpGet('/sectors/managements');
    if (status === 200 && Array.isArray(data)) {
      const managementParsed: IManagementOption[] = data.map((management: { id: number; initials: string }) => ({
        value: management.id,
        label: management.initials,
      }));
      setManagements(managementParsed);
    }
  }, [httpGet]);

  useEffect(() => {
    loadManagements();
  }, [loadManagements]);

  useEffect(() => {
    const allocId = user?.allocation?.management_id;
    if (!allocId || managements.length === 0) return;
    setSelectedManagement((prev) => {
      if (prev) return prev;
      return managements.find((m) => m.value === allocId) ?? null;
    });
  }, [managements, user]);

  const fetchList = useCallback(async (opts?: { silent?: boolean }) => {
    if (!managementId) return;
    if (!opts?.silent) {
      setListLoading(true);
    }
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
        page,
        perPage: 10,
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('orders/analytics/by-service-user/grouped-by-user', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
        && (res as { data: { data?: IAggregatedByUserRow[]; pagination?: IPaginate } }).data
      ) {
        const body = (res as { data: { data: IAggregatedByUserRow[]; pagination: IPaginate } }).data;
        setAggregatedRows(body.data || []);
        setPaginate(body.pagination);
      } else {
        setAggregatedRows([]);
      }
    } finally {
      if (!opts?.silent) {
        setListLoading(false);
      }
    }
  }, [managementId, appliedStart, appliedEnd, page, serviceUser, httpGet]);

  const fetchSummary = useCallback(async (opts?: { silent?: boolean }) => {
    if (!managementId) return;
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('orders/analytics/by-service-user/summary', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
      ) {
        const data = (res as { data: ITypeSummary }).data;
        setTypeSummary(data);
      } else {
        setTypeSummary({ person_count: 0, vehicle_count: 0, total: 0 });
      }
    } catch {
      setTypeSummary({ person_count: 0, vehicle_count: 0, total: 0 });
    }
  }, [managementId, appliedStart, appliedEnd, serviceUser, httpGet]);

  const fetchResultSummary = useCallback(async () => {
    if (!managementId) return;
    try {
      const params: Record<string, string | number> = {
        management_id: managementId,
        startDate: format(appliedStart, 'yyyy-MM-dd'),
        endDate: format(appliedEnd, 'yyyy-MM-dd'),
      };
      if (serviceUser?.value) {
        params.service_user_id = serviceUser.value;
      }

      const res = await httpGet('orders/analytics/by-service-user/summary-by-result', { params }, false);

      if (
        res
        && typeof res === 'object'
        && 'status' in res
        && (res as { status: number }).status === 200
        && 'data' in res
      ) {
        const payload = (res as { data: IResultSummaryRow[] | unknown }).data;
        setResultSummary(Array.isArray(payload) ? payload : []);
      } else {
        setResultSummary([]);
      }
    } catch {
      setResultSummary([]);
    }
  }, [managementId, appliedStart, appliedEnd, serviceUser, httpGet]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchResultSummary();
  }, [fetchResultSummary]);

  useEffect(() => {
    if (!managementId) return undefined;
    const timer = setInterval(() => {
      fetchList({ silent: true });
      fetchSummary({ silent: true });
      fetchResultSummary();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [managementId, fetchList, fetchSummary, fetchResultSummary]);

  const handleManagementChange = (opt: IManagementOption | null) => {
    setSelectedManagement(opt);
    setPage(1);
    setServiceUser(null);
  };

  const handleServiceUserChange = (opt: IServiceUserOption | null) => {
    setPage(1);
    setServiceUser(opt);
  };

  const handleFilter = () => {
    setAppliedStart(draftStart);
    setAppliedEnd(draftEnd);
    setPage(1);
  };

  const handlePaginate = (p: IPaginate) => {
    setPage(p.page);
  };

  const perPage = paginate.perPage || 10;

  const columns = useMemo(
    () => [
      {
        Header: '#',
        id: 'row_index',
        Cell: ({ row }: { row: { index: number } }) => (page - 1) * perPage + row.index + 1,
      },
      {
        Header: 'Usuário',
        id: 'nome',
        Cell: ({ row }: { row: { original: IAggregatedByUserRow } }) => {
          const o = row.original;
          return [o.first_name, o.last_name].filter(Boolean).join(' ').trim() || '—';
        },
      },
      {
        Header: 'Total',
        accessor: 'total',
      },
    ],
    [page, perPage],
  );

  const pieData = useMemo(() => {
    if (!typeSummary || typeSummary.total === 0) {
      return [];
    }
    const rows: { name: keyof typeof PIE_COLORS; value: number }[] = [];
    if (typeSummary.person_count > 0) {
      rows.push({ name: 'Pessoa', value: typeSummary.person_count });
    }
    if (typeSummary.vehicle_count > 0) {
      rows.push({ name: 'Veículo', value: typeSummary.vehicle_count });
    }
    return rows;
  }, [typeSummary]);

  const barChartHeight = useMemo(() => {
    const n = resultSummary.length;
    if (n === 0) return 140;
    const cap = isMdUp ? 480 : 320;
    return Math.min(cap, 72 + n * (isMdUp ? 38 : 32));
  }, [resultSummary.length, isMdUp]);

  const pieChartHeight = isMdUp ? 300 : 260;

  const links = [
    { path: '/analytics', title: "ANÁLISE", key: 1 },
    { path: '/proximity-dash', title: "PROXIMIDADE", key: 2 },
    { path: '/dashboard', title: "SAIR", key: 3 },
  ];

  return (
    <Laydash links={links}>
      {user?.allocation && (
        <Container>
          <FiltersRow>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Gerência
                </Typography>
                <ReactSelect<IManagementOption>
                  options={managements}
                  value={selectedManagement}
                  onChange={(opt) => handleManagementChange(opt as IManagementOption | null)}
                  placeholder="Selecione a gerência"
                  styles={analyticsSelectStyles}
                  noOptionsMessage={() => 'Nenhuma gerência'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Policial escalado no período (opcional)
                </Typography>
                <ReactSelect
                  options={serviceUserOptions}
                  value={serviceUser}
                  onChange={(opt) => handleServiceUserChange(opt as IServiceUserOption | null)}
                  placeholder="Todos escalados no período"
                  styles={analyticsSelectStyles}
                  isClearable
                  noOptionsMessage={() => 'Nenhum policial escalado neste período'}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Data inicial
                </Typography>
                <ReactDatePicker
                  selected={draftStart}
                  onChange={(d) => d && setDraftStart(d)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  maxDate={draftEnd}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} style={{ minWidth: 0 }}>
                <Typography variant="caption" display="block" gutterBottom>
                  Data final
                </Typography>
                <ReactDatePicker
                  selected={draftEnd}
                  onChange={(d) => d && setDraftEnd(d)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  minDate={draftStart}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} style={{ minWidth: 0 }}>
                <ButtonIcon type="button" title="Filtrar" onClick={handleFilter} isLoading={listLoading}>
                  <FilterIcon title="Filtrar" />
                </ButtonIcon>
              </Grid>
            </Grid>
          </FiltersRow>

          {managementId && (
            <ContentGrid>
              <LeftColumnPane>
                {aggregatedRows.length === 0 && !listLoading && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Nenhum registro agregado no período selecionado.
                  </Typography>
                )}
                {aggregatedRows.length > 0 && (
                  <TableSection>
                    <TableScrollBox>
                      <Table columns={columns} data={aggregatedRows} hidePagination />
                    </TableScrollBox>
                    <Pagination paginate={paginate} handlePaginate={handlePaginate} />
                  </TableSection>
                )}
              </LeftColumnPane>
              <RightColumnPane>
                <ChartsColumn>
                  <PieSummaryRow>
                    <ChartPanel>
                      <ChartTitle>Distribuição por tipo (período filtrado)</ChartTitle>
                      {pieData.length === 0 ? (
                        <Typography variant="body2" style={{ color: '#aaa' }}>
                          Sem dados no período para o gráfico.
                        </Typography>
                      ) : (
                        <ResponsiveContainer width="100%" height={pieChartHeight}>
                          <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="45%"
                              outerRadius={isMdUp ? 88 : 64}
                              labelLine={false}
                              label={({
                                name, percent,
                              }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            >
                              {pieData.map((entry) => (
                                <Cell key={entry.name} fill={PIE_COLORS[entry.name]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number, name: string) => [`${value} consultas`, name]}
                            />
                            <Legend
                              layout="vertical"
                              verticalAlign="bottom"
                              align="center"
                              iconSize={10}
                              wrapperStyle={{
                                fontSize: 11,
                                lineHeight: '18px',
                                paddingTop: 4,
                              }}
                              formatter={(value: string) => (value.length > 36 ? `${value.slice(0, 33)}…` : value)}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </ChartPanel>

                    <SummaryPanel>
                      <ChartTitle>Resumo por tipo</ChartTitle>
                      <table>
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Resultado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Pessoa</td>
                            <td>{typeSummary?.person_count ?? 0}</td>
                          </tr>
                          <tr>
                            <td>Veículo</td>
                            <td>{typeSummary?.vehicle_count ?? 0}</td>
                          </tr>
                          <tr>
                            <td>Total geral</td>
                            <td>{typeSummary?.total ?? 0}</td>
                          </tr>
                        </tbody>
                      </table>
                    </SummaryPanel>
                  </PieSummaryRow>

                  <BarChartPanel>
                    <ChartTitle>Resultado (quantidade por apuração)</ChartTitle>
                    {resultSummary.length === 0 ? (
                      <Typography variant="body2" style={{ color: '#aaa' }}>
                        Sem dados no período para o gráfico de barras.
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={barChartHeight}>
                        <BarChart
                          layout="vertical"
                          data={resultSummary}
                          margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                          <XAxis type="number" stroke="#aaa" allowDecimals={false} />
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={isMdUp ? 160 : 120}
                            tick={{ fill: '#ddd', fontSize: 11 }}
                            interval={0}
                          />
                          <Tooltip
                            formatter={(value: number) => [`${value} consultas`, 'Total']}
                            labelFormatter={(label: string) => label}
                          />
                          <Bar dataKey="count" name="Quantidade" radius={[0, 4, 4, 0]}>
                            {resultSummary.map((row, i) => (
                              <Cell key={row.name} fill={BAR_PALETTE[i % BAR_PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </BarChartPanel>
                </ChartsColumn>
              </RightColumnPane>
            </ContentGrid>
          )}
        </Container>
      )}
    </Laydash>
  );
};

export default Analytics;
